/**
 * The `ItemId` ↔ `EntityRef` bridge — the ONE sanctioned cast site between
 * the content id family and the parser's opaque reference brand.
 *
 * @remarks
 * The parser deliberately knows nothing about items (architecture.md §4.4):
 * it resolves noun phrases against `ParseCandidateEntity` catalog entries and
 * hands back the `entityRef` the engine supplied. The engine's convention is
 * that a catalog entry's ref IS the item's id, re-branded — collision-free
 * because content ids are globally unique within a pack (D11), and reversible
 * without any lookup table. Both directions funnel through these two helpers
 * so the double-cast (`as unknown as`) appears exactly once per direction in
 * the codebase; casting anywhere else is a review defect.
 *
 * A ref arriving back from the parser is only trusted as far as step 2 of the
 * turn pipeline allows: `executeTurn` re-validates every ref against the
 * CURRENT turn's catalog before acting on it (architecture.md §7.2).
 *
 * @packageDocumentation
 */

import type { ActorId, ItemId, RoomId } from '@orpheus/content-schema';
import type { EntityRef } from '@orpheus/parser-engine';

/**
 * Brand an item id as the parser-facing entity reference for its catalog
 * entry.
 *
 * @param itemId - The item's content id
 * @returns The same string, carrying the parser's `EntityRef` brand
 */
export const entityRefForItem = (itemId: ItemId): EntityRef => itemId as unknown as EntityRef;

/**
 * Recover the item id inside a parser entity reference.
 *
 * @param entityRef - A ref previously issued by {@link entityRefForItem}
 * @returns The same string, carrying the content `ItemId` brand
 */
export const itemIdForEntityRef = (entityRef: EntityRef): ItemId => entityRef as unknown as ItemId;

/**
 * The prefix distinguishing a ROOM reference from an item one (D41). Item ids
 * cannot collide with it: the pack schema forbids `:` in content ids, so the
 * two namespaces stay disjoint by construction.
 */
const ROOM_REF_PREFIX = 'room-ref:';

/**
 * What the engine CALLS the room when a response names it (D41).
 *
 * @remarks
 * Never the room's authored name. Every slot that can be handed a room frames
 * its subject — "The {itemName} does not answer to TAKE", "The {itemName},
 * then." — and a room called "The Hall" would read "The The Hall". This
 * phrasing survives both frames, and lives here so the refusal and the
 * bare-noun prompt cannot drift apart.
 */
export const ROOM_ITSELF_NAME = 'room itself';

/**
 * The words every game's current room answers to (D41) — the engine's own,
 * declared by no pack: "search the room", "look at here", "go over the
 * place". A room's authored `name` and `nouns` join these in the catalog.
 *
 * @remarks
 * Lives beside {@link ROOM_ITSELF_NAME} because the two are one rule read
 * from both ends (D226, E20): a refusal that names the room quotes the
 * player's word when it was the ROOM'S OWN word (`the ruts`, `the track`),
 * and the engine's framing when it was one of these — "Nothing about here
 * answers to USE" is not a sentence.
 */
export const ENGINE_ROOM_WORDS: readonly string[] = ['room', 'here', 'area', 'place', 'surroundings', 'space'];

/**
 * What a response calls the room the player named (D226, E20): their own
 * word for it when it was the room's — its authored name or one of its
 * `nouns` — and {@link ROOM_ITSELF_NAME} when it was one of the engine's
 * {@link ENGINE_ROOM_WORDS}, or when no word is known (a command assembled
 * without a phrase).
 *
 * @param playerWord - The head the player typed for the room, when the parser kept it
 * @returns The name to put in the slot's `{itemName}`
 */
export const roomNameInPlayersWord = (playerWord: string | undefined): string =>
	playerWord === undefined || ENGINE_ROOM_WORDS.includes(playerWord) ? ROOM_ITSELF_NAME : playerWord;

/**
 * Brand the ACTIVE room as a referable entity (D41) — "search the room",
 * "look at here". The room the player stands in is the one thing every game
 * has and no catalog listed.
 *
 * @param roomId - The room's content id
 * @returns The parser-facing reference for it
 */
export const entityRefForRoom = (roomId: RoomId): EntityRef => `${ROOM_REF_PREFIX}${roomId}` as unknown as EntityRef;

/**
 * The one BODY reference (D63): the player's hands, referable in every game.
 *
 * @remarks
 * D53's own refusal invited this — `open the locker` asks "Open it with
 * what?", and "hands" is the answer a person gives. An engine that asks a
 * question must accept its natural answers, and "Nothing here answers to
 * \"hands\"" was the engine failing its own conversation. Same namespace
 * trick as the room ref: pack ids cannot contain `:`, so no collision.
 */
export const BODY_REF: EntityRef = 'body-ref:hands' as unknown as EntityRef;

/**
 * What the engine CALLS the hands when a response names them (D63).
 *
 * @remarks
 * Same framing rule as {@link ROOM_ITSELF_NAME}: every slot that can be handed
 * this frames its subject with an article — "Nothing about the {itemName}
 * answers…" — so "your hands" would read "the your hands". "Hands themselves"
 * survives the frame, and reads as the aside it is.
 */
export const BODY_NAME = 'hands themselves';

/**
 * Is this reference the player's own hands (D63)?
 *
 * @param entityRef - Any resolved reference
 * @returns Whether it names the body ref
 */
export const isBodyRef = (entityRef: EntityRef): boolean => (entityRef as unknown as string) === (BODY_REF as unknown as string);

/**
 * The room id inside a room reference, or `undefined` when the ref names an
 * item.
 *
 * @param entityRef - A ref the parser resolved
 * @returns The room id, or `undefined`
 */
export const roomIdForEntityRef = (entityRef: EntityRef): RoomId | undefined => {
	const refText: string = entityRef;

	return refText.startsWith(ROOM_REF_PREFIX) ? (refText.slice(ROOM_REF_PREFIX.length) as RoomId) : undefined;
};

/**
 * The prefix of a TOPIC-WORD reference (D231): a subject the player may ask
 * about that is no thing at all — a plain key of some present thing's
 * topic table (`directive`, `steps`). Same namespace trick as the room and
 * body refs: pack ids cannot contain `:`, so no collision.
 */
const TOPIC_WORD_REF_PREFIX = 'topic-word:';

/**
 * Brand a topic key as a referable entity for the parse catalog (D231), so
 * `ask the voice about the directive` resolves when no item is called that.
 *
 * @param word - The key, folded as the catalog folds it
 * @returns The parser-facing reference for it
 */
export const entityRefForTopicWord = (word: string): EntityRef => `${TOPIC_WORD_REF_PREFIX}${word}` as unknown as EntityRef;

/**
 * The topic word inside a topic-word reference, or `undefined` when the ref
 * names an item, the room or the hands.
 *
 * @param entityRef - A ref the parser resolved
 * @returns The word, or `undefined`
 */
export const topicWordForEntityRef = (entityRef: EntityRef): string | undefined => {
	const refText: string = entityRef;

	return refText.startsWith(TOPIC_WORD_REF_PREFIX) ? refText.slice(TOPIC_WORD_REF_PREFIX.length) : undefined;
};

/**
 * The prefix of an ACTOR reference (D232): a body in the room, a new entity
 * kind beside items (`docs/design/actors.md` §2). Same namespace trick as
 * the room, body and topic-word refs — pack ids cannot contain `:` — so the
 * kind is read off the ref itself, and no lookup is needed to know that a
 * reference the parser hands back names a person rather than a thing. The
 * item verbs' narrowing reads this before it ever reaches `requireItem`.
 */
const ACTOR_REF_PREFIX = 'actor-ref:';

/**
 * Brand an actor id as the parser-facing entity reference for its catalog
 * entry (D232).
 *
 * @param actorId - The actor's content id
 * @returns The parser-facing reference for it
 */
export const entityRefForActor = (actorId: ActorId): EntityRef => `${ACTOR_REF_PREFIX}${actorId}` as unknown as EntityRef;

/**
 * The actor id inside an actor reference, or `undefined` when the ref names
 * anything else (D232).
 *
 * @param entityRef - A ref the parser resolved
 * @returns The actor id, or `undefined`
 */
export const actorIdForEntityRef = (entityRef: EntityRef): ActorId | undefined => {
	const refText: string = entityRef;

	return refText.startsWith(ACTOR_REF_PREFIX) ? (refText.slice(ACTOR_REF_PREFIX.length) as ActorId) : undefined;
};
